import api from '.'

/**
 * Provides methods for handling administrative tasks related to organization accounts.
 * This class includes functionalities to create, update, delete, and retrieve organization account information.
 */
class AdminOrganizationAccountApi {
    /**
     * Creates a new organization account with the specified details.
     *
     * @param {string} organization_id - The ID of the organization.
     * @param {string} organization_account_id - The ID of the organization account.
     * @param {string} parent_organization_account_id - The ID of the parent organization account.
     * @param {string} organization_billing_profile_id - The ID of the organization billing profile.
     * @param {string} account_name - The name of the account.
     * @param {string} account_type - The type of the account.
     * @param {boolean} is_enabled - The status of the account (enabled or disabled).
     * @returns {Promise<Object>} The response data from the API call.
     */
    create(
        organization_id,
        organization_account_id,
        parent_organization_account_id,
        organization_billing_profile_id,
        account_name,
        account_type,
        is_enabled
    ) {
        const payload = {
            organization_id,
            organization_account_id,
            parent_organization_account_id,
            organization_billing_profile_id,
            account_name,
            account_type,
            is_enabled
        }

        return api
            .sentRequest(
                '/api/organization/account/admin/create',
                payload,
                true
            )
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Updates the organization account with the provided details.
     *
     * @param {string} organization_id - The ID of the organization.
     * @param {string} organization_account_id - The ID of the organization account.
     * @param {string} parent_organization_account_id - The ID of the parent organization account.
     * @param {string} organization_billing_profile_id - The ID of the organization's billing profile.
     * @param {string} account_name - The name of the account.
     * @param {string} account_type - The type of the account.
     * @param {boolean} is_enabled - Indicates if the account is enabled.
     * @returns {Promise<Object>} A promise that resolves to the updated account data or an error response.
     */
    update(
        organization_id,
        organization_account_id,
        parent_organization_account_id,
        organization_billing_profile_id,
        account_name,
        account_type,
        is_enabled
    ) {
        const payload = {
            organization_id,
            organization_account_id,
            parent_organization_account_id,
            organization_billing_profile_id,
            account_name,
            account_type,
            is_enabled
        }

        return api
            .sentRequest(
                '/api/organization/account/admin/update',
                payload,
                true
            )
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }
}

export const adminOrganizationAccountApi = new AdminOrganizationAccountApi()
